// 额度换算与展示（与内部参考实现 helpers/quota 行为对齐）

import { useStatusStore } from '@/stores/statusStore'
import { getNumberLocale } from './locale'

type CurrencyType = 'USD' | 'CNY' | 'CUSTOM' | 'TOKENS'

interface CurrencyConfig {
  type: CurrencyType
  rate: number
  prefix: string
  suffix: string
}

/**
 * 读取额度单位（每 1 美金对应多少 quota）
 * 优先走 StatusStore，兜底 localStorage
 */
export function getQuotaPerUnit(): number {
  const fromStore = useStatusStore.getState().status?.quota_per_unit
  if (typeof fromStore === 'number' && fromStore > 0) return fromStore
  const raw = parseFloat(localStorage.getItem('quota_per_unit') || '500000')
  return Number.isFinite(raw) && raw > 0 ? raw : 500000
}

/**
 * 货币配置：是否按人民币展示、兑换倍率
 */
export function getCurrencyConfig(): CurrencyConfig {
  const status = useStatusStore.getState().status
  const displayType = (status?.quota_display_type || '').toUpperCase()
  const displayInCurrency = status?.display_in_currency ?? true

  if (displayType === 'TOKENS' || (!displayType && !displayInCurrency)) {
    return { type: 'TOKENS', rate: 1, prefix: '', suffix: ' tokens' }
  }
  if (displayType === 'CNY') {
    const rate = Number(status?.usd_exchange_rate) || 1
    return { type: 'CNY', rate, prefix: '¥', suffix: '' }
  }
  if (displayType === 'CUSTOM') {
    const rate = Number(status?.custom_currency_exchange_rate) || 1
    return {
      type: 'CUSTOM',
      rate,
      prefix: status?.custom_currency_symbol || '¤',
      suffix: '',
    }
  }
  return { type: 'USD', rate: 1, prefix: '$', suffix: '' }
}

/** 把美元金额按当前展示货币格式化（定价页用；不做 quota 换算） */
export function formatUsdAsDisplay(usd: number | undefined | null, digits = 4): string {
  const v = Number(usd)
  if (!Number.isFinite(v)) return '-'
  const { type, rate, prefix, suffix } = getCurrencyConfig()
  if (type === 'TOKENS') return `$${v.toString()}`
  const converted = type === 'USD' ? v : v * (rate || 1)
  const fixed = Math.abs(converted) >= 1 ? converted.toFixed(2) : converted.toFixed(digits)
  return `${prefix}${fixed.replace(/0+$/, '').replace(/\.$/, '')}${suffix}`
}

/**
 * 把 quota 转为展示金额
 */
export function quotaToDisplayAmount(quota: number | undefined | null): number {
  const q = Number(quota || 0)
  if (!Number.isFinite(q) || q <= 0) return 0
  const { type, rate } = getCurrencyConfig()
  if (type === 'TOKENS') return q
  const usd = q / getQuotaPerUnit()
  if (type === 'USD') return usd
  return usd * (rate || 1)
}

/**
 * 展示金额转 quota
 */
export function displayAmountToQuota(amount: number): number {
  const val = Number(amount || 0)
  if (!Number.isFinite(val) || val <= 0) return 0
  const { type, rate } = getCurrencyConfig()
  if (type === 'TOKENS') return Math.round(val)
  const usd = type === 'USD' ? val : val / (rate || 1)
  return Math.round(usd * getQuotaPerUnit())
}

/** 1 USD = 1_000_000 micro USD */
export const MICRO_USD_PER_USD = 1_000_000

/** USD 浮点金额 → micro USD 整数 */
export function usdToMicroUsd(usd: number | undefined | null): number {
  const v = Number(usd)
  if (!Number.isFinite(v)) return 0
  return Math.round(v * MICRO_USD_PER_USD)
}

/** micro USD 整数 → USD 浮点 */
export function microUsdToUsd(micro: number | undefined | null): number {
  const v = Number(micro)
  if (!Number.isFinite(v)) return 0
  return v / MICRO_USD_PER_USD
}

/**
 * 格式化上游渠道余额（USD 浮点，非内部 quota 单位）
 */
export function formatUsd(usd: number | undefined | null, digits = 2): string {
  const v = Number(usd)
  if (!Number.isFinite(v)) return '-'
  return `$${v.toFixed(digits)}`
}

/**
 * 格式化 micro USD 为美元字符串
 */
export function formatMicroUsd(micro: number | undefined | null, digits = 2): string {
  return formatUsd(microUsdToUsd(micro), digits)
}

/**
 * 格式化 quota 为带符号的字符串
 */
export function renderQuota(quota: number | undefined | null, digits = 2): string {
  const { type, prefix, suffix } = getCurrencyConfig()
  const amount = quotaToDisplayAmount(quota)
  if (type === 'TOKENS') {
    return `${Math.round(amount).toLocaleString(getNumberLocale())}${suffix}`
  }
  return `${prefix}${amount.toFixed(digits)}${suffix}`
}

/**
 * 紧凑版 quota 展示（K/M 单位），用于卡片
 */
export function renderQuotaCompact(quota: number | undefined | null): string {
  const { type, prefix } = getCurrencyConfig()
  const amount = quotaToDisplayAmount(quota)
  if (type === 'TOKENS') {
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(2)}M`
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(2)}K`
    return Math.round(amount).toString()
  }
  return `${prefix}${amount.toFixed(2)}`
}

/**
 * 数字千分位格式化
 */
export function formatNumberCompact(value: number | undefined | null): string {
  const v = Number(value || 0)
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`
  if (v >= 10_000) return `${(v / 1_000).toFixed(1)}K`
  return v.toLocaleString(getNumberLocale())
}
